import { LocalStorage } from 'quasar';
import { defineStore } from 'pinia';
import api from 'src/lib/api/client';
import AccountService from 'src/lib/api/accountService';
import UserService from 'src/lib/api/userService';
import constants from 'src/lib/constants';

export const useAccountStore = defineStore('account', {
  state: () => ({
    details: null,
    settings: null,
    accessToken: null,
    pushToken: null,
    temporarySubscription: false,
  }),

  getters: {
    isAdmin() {
      return this.details?.role === 'admin';
    },

    isCustomer() {
      return this.details?.role === 'customer';
    },

    hasSubscription() {
      if (this.isAdmin) {
        return true;
      }
      if (this.isCustomer) {
        if (this.temporarySubscription) {
          return true;
        }
        if (this.subscriptionEndDate && this.subscriptionEndDate > new Date()) {
          return true;
        }
      }

      return false;
    },

    subscriptionEndDate() {
      if (this.details?.current_subscription?.end_date) {
        return new Date(this.details.current_subscription?.end_date);
      }
      return null;
    },
  },

  actions: {
    async setApiAccessToken(accessToken) {
      if (accessToken) {
        api.axiosConfigDefaults.headers.Authorization = `Bearer ${accessToken}`;
      }
      else {
        api.axiosConfigDefaults.headers.Authorization = '';
      }

      await api.init();
    },

    async login({ email, password, deviceName }) {
      const response = await AccountService.login({
        email,
        password,
        deviceName,
      });

      if (response.status === 200) {
        this.accessToken = response.data.token;
        await this.setApiAccessToken(this.accessToken);

        this.details = response.data.data;

        this.storeCredentials({
          token: this.accessToken,
          account: this.details,
        });
      }

      if (this.pushToken) {
        await UserService.updatePushToken({
          userId: this.details.id,
          token: this.pushToken,
        });
      }
    },

    logout() {
      LocalStorage.remove(constants.LOCAL_STORAGE_ACCESS_TOKEN);
      LocalStorage.remove(constants.LOCAL_STORAGE_ACCOUNT);

      this.setApiAccessToken(null);

      this.details = null;
      this.accessToken = null;
      this.pushToken = null;
    },

    async getAccount({ id }) {
      const response = await UserService.getUser({ id });
      if (response.status === 200) {
        this.details = response.data;
      }
    },

    storeCredentials({ token, account }) {
      LocalStorage.set(constants.LOCAL_STORAGE_ACCESS_TOKEN, token);
      LocalStorage.set(constants.LOCAL_STORAGE_ACCOUNT, account);
    },

    async loadCredentials() {
      if (LocalStorage.has(constants.LOCAL_STORAGE_ACCESS_TOKEN)) {
        const account = LocalStorage.getItem(constants.LOCAL_STORAGE_ACCOUNT);
        this.accessToken = LocalStorage.getItem(constants.LOCAL_STORAGE_ACCESS_TOKEN);
        await this.setApiAccessToken(this.accessToken);
        await this.getAccount({ id: account.id });
      }
    },

    loadSettings() {
      const localStorageSettings = LocalStorage.getItem(constants.LOCAL_STORAGE_SETTINGS);
      if (localStorageSettings && localStorageSettings[this.details.id]) {
        this.settings = localStorageSettings[this.details.id];
      }
      else {
        this.settings = {
          goals: 'macros',
        };
      }
    },

    async passwordTokenCreate({ email }) {
      await AccountService.passwordTokenCreate({ email });
    },

    async passwordTokenVerify({ email, code }) {
      await AccountService.passwordTokenVerify({ email, code });
    },

    async passwordTokenUpdate({
      email, code, deviceName, password, passwordConfirmation,
    }) {
      const response = await AccountService.passwordTokenUpdate({
        email, code, deviceName, password, passwordConfirmation,
      });
      if (response.status === 200) {
        this.accessToken = response.data.token;
        await this.setApiAccessToken(this.accessToken);

        this.details = response.data.data;

        this.storeCredentials({
          token: this.accessToken,
          account: this.details,
        });
      }

      if (this.pushToken) {
        await UserService.updatePushToken({
          userId: this.details.id,
          token: this.pushToken,
        });
      }
    },
  },
});
